/* eslint-disable no-undef */
import React, { useEffect, useRef, useState } from "react";
import styles from "../Assets/css/fleet.module.css";
import { Loader } from "@googlemaps/js-api-loader";

const USA_BOUNDS = {
  north: 52,
  south: 24,
  west: -125.0,
  east: -66.5,
};

const STATE_COORDINATES = {
  "Alabama": { lat: 32.806671, lng: -86.791130, zoom: 7 },
  "Alaska": { lat: 61.370716, lng: -152.404419, zoom: 4 },
  "Arizona": { lat: 33.729759, lng: -111.431221, zoom: 7 },
  "Arkansas": { lat: 34.969704, lng: -92.373123, zoom: 7 },
  "California": { lat: 36.116203, lng: -119.681564, zoom: 6 },
  "Colorado": { lat: 39.059811, lng: -105.311104, zoom: 7 },
  "Connecticut": { lat: 41.597782, lng: -72.755371, zoom: 9 },
  "Delaware": { lat: 39.318523, lng: -75.507141, zoom: 9 },
  "Florida": { lat: 27.766279, lng: -81.686783, zoom: 6 },
  "Georgia": { lat: 33.040619, lng: -83.643074, zoom: 7 },
  "Hawaii": { lat: 21.094318, lng: -157.498337, zoom: 7 },
  "Idaho": { lat: 44.240459, lng: -114.478828, zoom: 6 },
  "Illinois": { lat: 40.349457, lng: -88.986137, zoom: 7 },
  "Indiana": { lat: 39.849426, lng: -86.258278, zoom: 7 },
  "Iowa": { lat: 42.011539, lng: -93.210526, zoom: 7 },
  "Kansas": { lat: 38.526600, lng: -96.726486, zoom: 7 },
  "Kentucky": { lat: 37.668140, lng: -84.670067, zoom: 7 },
  "Louisiana": { lat: 31.169546, lng: -91.867805, zoom: 7 },
  "Maine": { lat: 44.693947, lng: -69.381927, zoom: 7 },
  "Maryland": { lat: 39.063946, lng: -76.802101, zoom: 8 },
  "Massachusetts": { lat: 42.230171, lng: -71.530106, zoom: 8 },
  "Michigan": { lat: 43.326618, lng: -84.536095, zoom: 6 },
  "Minnesota": { lat: 45.694454, lng: -93.900192, zoom: 6 },
  "Mississippi": { lat: 32.741646, lng: -89.678696, zoom: 7 },
  "Missouri": { lat: 38.456085, lng: -92.288368, zoom: 7 },
  "Montana": { lat: 46.921925, lng: -110.454353, zoom: 6 },
  "Nebraska": { lat: 41.125370, lng: -98.268082, zoom: 7 },
  "Nevada": { lat: 38.313515, lng: -117.055374, zoom: 6 },
  "New Hampshire": { lat: 43.452492, lng: -71.563896, zoom: 8 },
  "New Jersey": { lat: 40.298904, lng: -74.521011, zoom: 10 },
  "New Mexico": { lat: 34.840515, lng: -106.248482, zoom: 7 },
  "New York": { lat: 42.165726, lng: -74.948051, zoom: 7 },
  "North Carolina": { lat: 35.630066, lng: -79.806419, zoom: 7 },
  "North Dakota": { lat: 47.528912, lng: -99.784012, zoom: 7 },
  "Ohio": { lat: 40.388783, lng: -82.764915, zoom: 7 },
  "Oklahoma": { lat: 35.565342, lng: -96.928917, zoom: 7 },
  "Oregon": { lat: 44.572021, lng: -122.070938, zoom: 7 },
  "Pennsylvania": { lat: 40.590752, lng: -77.209755, zoom: 7 },
  "Rhode Island": { lat: 41.680893, lng: -71.511780, zoom: 10 },
  "South Carolina": { lat: 33.856892, lng: -80.945007, zoom: 7 },
  "South Dakota": { lat: 44.299782, lng: -99.438828, zoom: 7 },
  "Tennessee": { lat: 35.747845, lng: -86.692345, zoom: 7 },
  "Texas": { lat: 31.054487, lng: -97.563461, zoom: 6 },
  "Utah": { lat: 40.150032, lng: -111.862434, zoom: 7 },
  "Vermont": { lat: 44.045876, lng: -72.710686, zoom: 8 },
  "Virginia": { lat: 37.769337, lng: -78.169968, zoom: 7 },
  "Washington": { lat: 47.400902, lng: -121.490494, zoom: 7 },
  "West Virginia": { lat: 38.491226, lng: -80.954453, zoom: 7 },
  "Wisconsin": { lat: 44.268543, lng: -89.616508, zoom: 7 },
  "Wyoming": { lat: 42.755966, lng: -107.302490, zoom: 7 }
};

const PPAMap = ({
  unifiedDevices = [],
  enphaseDevices = [],
  selectedState = "All",
  selectedUtility = "All",
  utilities = [],
  selectedDaylight = "Daylight"
}) => {
  const GOOGLE_MAP_API_KEY = process.env.REACT_APP_GOOGLE_MAP_API_KEY;
  const [selectedDeviceType, setSelectedDeviceType] = useState("All");
  const mapContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [infoWindow, setInfoWindow] = useState(null);
  const [weatherData, setWeatherData] = useState(null);
  const [searchedLocation, setSearchedLocation] = useState(null);

  const handleSelectChange = (event) => {
    setSelectedDeviceType(event.target.value);
  };

  const fetchWeatherData = async (lat, lng, locationName) => {
    try {
      const WEATHER_API_KEY = process.env.REACT_APP_WEATHER_API_KEY;
      const response = await fetch(
        `https://api.weatherapi.com/v1/current.json?key=${WEATHER_API_KEY}&q=${lat},${lng}&aqi=no`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch weather data: ${response.status}`);
      }
      const data = await response.json();
      setWeatherData({
        temp: Math.round(data.current.temp_f),
        feelsLike: Math.round(data.current.feelslike_f),
        condition: data.current.condition.text,
        description: data.current.condition.text.toLowerCase(),
        humidity: data.current.humidity,
        windSpeed: Math.round(data.current.wind_mph),
        icon: data.current.condition.icon,
        error: false,
      });
      setSearchedLocation(locationName);
    } catch (error) {
      console.error("Error fetching weather data:", error);
      setWeatherData({
        temp: "--",
        feelsLike: "--",
        condition: "Error",
        description: error.message || "Unable to fetch weather data",
        humidity: "--",
        windSpeed: "--",
        icon: "",
        error: true,
      });
      setSearchedLocation(locationName);
    }
  };

  // Load Google Maps
  useEffect(() => {
    const loader = new Loader({
      apiKey: GOOGLE_MAP_API_KEY,
      version: "weekly",
      libraries: ["places"],
    });

    loader.load().then(() => {
      const newMap = new google.maps.Map(mapContainerRef.current, {
        center: { lat: 39.8, lng: -98.6 },
        zoom: 4,
        minZoom: 0,
        maxZoom: 18,
        restriction: {
          latLngBounds: USA_BOUNDS,
          strictBounds: true,
        },
        gestureHandling: "greedy",
        mapTypeControl: true,
        streetViewControl: true,
        mapTypeControlOptions: {
          position: google.maps.ControlPosition.BOTTOM_LEFT,
        },
        fullscreenControl: true,
        fullscreenControlOptions: {
          position: google.maps.ControlPosition.BOTTOM_RIGHT,
        },
      });

      setMap(newMap);
      setInfoWindow(new google.maps.InfoWindow());

      // Autocomplete
      const input = searchInputRef.current;
      const auto = new google.maps.places.Autocomplete(input, {
        types: ["geocode", "establishment"],
        fields: ["place_id", "geometry", "name", "formatted_address"],
      });
      auto.setComponentRestrictions({ country: ["us"] });
      auto.addListener("place_changed", () => {
        const place = auto.getPlace();
        if (!place.geometry || !place.geometry.viewport) return;

        const location = place.geometry.location;
        fetchWeatherData(location.lat(), location.lng(), place.formatted_address || place.name);

        if (place.geometry.viewport) {
          newMap.fitBounds(place.geometry.viewport);
        } else {
          newMap.setCenter(place.geometry.location);
          newMap.setZoom(12);
        }
        setTimeout(() => {
          if (newMap.getZoom() > 20) newMap.setZoom(20);
        }, 500);
      });
    });
  }, []);

  // Center map on selected state
  useEffect(() => {
    if (!map) return;
    if (selectedState && selectedState !== "All" && STATE_COORDINATES[selectedState]) {
      const stateCoords = STATE_COORDINATES[selectedState];
      map.setCenter({ lat: stateCoords.lat, lng: stateCoords.lng });
      map.setZoom(stateCoords.zoom);
    } else if (selectedState === "All") {
      map.setCenter({ lat: 39.8, lng: -98.6 });
      map.setZoom(4);
    }
  }, [map, selectedState]);

  // Update markers
  useEffect(() => {
    if (!map || !infoWindow) return;

    const getColor = (type) => {
      const t = type?.toLowerCase();
      if (t.includes("hoymiles")) return "#FF9800"; // Orange
      if (t.includes("sol-ark") || t.includes("solark")) return "#9C27B0"; // Purple
      if (t.includes("enphase")) return "#4285F4"; // Blue
      if (t.includes("franklin")) return "#9E9E9E"; // Gray
      return "#FBBC05"; // Yellow (default)
    };

    // Only PPA device types: Hoymiles, Sol-Ark, Franklin
    const ppaTypes = ["Hoymiles", "Sol-Ark", "Franklin"];

    // Daylight types: Hoymiles, Sol-Ark, Enphase
    // AppUser types: Franklin
    const daylightUnifiedTypes = ["Hoymiles", "Sol-Ark"];
    const nonDaylightUnifiedTypes = ["Franklin"];

    // Build Enphase markers from texture Inverter devices
    const enphaseMarkers = (enphaseDevices || [])
      .filter(d => {
        const lat = d.owner_address?.latitude || d.address?.[0]?.latitude;
        const lng = d.owner_address?.longitude || d.address?.[0]?.longitude;
        return lat && lng;
      })
      .map(d => ({
        name: d.name || "Enphase Device",
        lat: parseFloat(d.owner_address?.latitude || d.address?.[0]?.latitude),
        lng: parseFloat(d.owner_address?.longitude || d.address?.[0]?.longitude),
        type: "enphase",
        state: d.owner_address?.state || d.address?.[0]?.state,
        zip: d.owner_address?.zip_code || d.address?.[0]?.zip_code
      }));

    // Determine which unified types and whether to include enphase based on daylight filter
    let allowedUnifiedTypes = ppaTypes;
    let includeEnphase = true;

    if (selectedDaylight === "Daylight") {
      allowedUnifiedTypes = daylightUnifiedTypes;
      includeEnphase = true;
    } else if (selectedDaylight === "AppUser") {
      allowedUnifiedTypes = nonDaylightUnifiedTypes;
      includeEnphase = false;
    }

    let filteredData = [];

    if (selectedDeviceType === "All") {
      const unifiedMarkers = (unifiedDevices || [])
        .filter(d => d.lat && d.lon && allowedUnifiedTypes.includes(d.type))
        .map(d => ({
          name: d.name || d.device_id,
          lat: parseFloat(d.lat),
          lng: parseFloat(d.lon),
          type: d.type.toLowerCase(),
          state: null,
          zip: null
        }));
      filteredData = includeEnphase
        ? [...unifiedMarkers, ...enphaseMarkers]
        : [...unifiedMarkers];
    } else if (selectedDeviceType === "Enphase") {
      filteredData = includeEnphase ? enphaseMarkers : [];
    } else {
      // Check if the selected device type is allowed by the daylight filter
      const isAllowed = allowedUnifiedTypes.includes(selectedDeviceType);
      if (isAllowed) {
        filteredData = (unifiedDevices || [])
          .filter(d => d.type === selectedDeviceType && d.lat && d.lon)
          .map(d => ({
            name: d.name || d.device_id,
            lat: parseFloat(d.lat),
            lng: parseFloat(d.lon),
            type: d.type.toLowerCase(),
            state: null,
            zip: null
          }));
      }
    }

    // Filter by state
    if (selectedState !== "All") {
      filteredData = filteredData.filter(item =>
        item.state && item.state.toLowerCase() === selectedState.toLowerCase()
      );
    }

    // Filter by utility
    if (selectedUtility !== "All") {
      const utility = utilities.find(u => u.id.toString() === selectedUtility);
      if (utility && utility.zipcodes) {
        const allowedZips = utility.zipcodes.map(z => typeof z === "string" ? z : z.zip_code);
        filteredData = filteredData.filter(item => item.zip && allowedZips.includes(item.zip));
      }
    }

    // Filter by USA Bounds
    filteredData = filteredData.filter(item =>
      item.lat <= USA_BOUNDS.north &&
      item.lat >= USA_BOUNDS.south &&
      item.lng >= USA_BOUNDS.west &&
      item.lng <= USA_BOUNDS.east
    );

    // Clear old markers
    markers.forEach(m => m.setMap(null));
    setMarkers([]);

    // Add new markers
    const newMarkers = filteredData.map(item => {
      const marker = new google.maps.Marker({
        position: { lat: item.lat, lng: item.lng },
        map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: getColor(item.type),
          fillOpacity: 0.9,
          strokeColor: "#fff",
          strokeWeight: 2,
          scale: 11,
        },
        title: item.name,
      });

      marker.addListener("click", () => {
        infoWindow.setContent(
          `<div style="font-family: Arial; padding: 5px;">
            <strong>${item.name}</strong><br/>
            <small style="color: #666;">${item.type.replace(/-/g, " ")}</small>
          </div>`
        );
        infoWindow.open(map, marker);
      });

      return marker;
    });

    setMarkers(newMarkers);

    // Auto fit
    if (newMarkers.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      newMarkers.forEach(m => bounds.extend(m.getPosition()));
      map.fitBounds(bounds);
      if (map.getZoom() > 16) map.setZoom(16);
    }
  }, [map, infoWindow, unifiedDevices, enphaseDevices, selectedDeviceType, selectedState, selectedUtility, utilities, selectedDaylight]);

  return (
    <>
      {/* Search bar */}
      <div
        style={{
          position: "absolute",
          top: 10,
          left: "clamp(44px, 23vw, 110px)",
          transform: "translateX(-50%)",
          zIndex: 10,
          background: "white",
          boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
          height: "36px",
          width: "200px",
          maxWidth: "90%",
          display: "flex",
          alignItems: "center",
          borderRadius: "3px",
          outline: "none",
          backgroundColor: "#fff7e9",
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#5f6368"
          strokeWidth="2.5"
          style={{ marginRight: "10px", flexShrink: 0, marginLeft: "10px" }}
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search Address"
          style={{
            width: "100%",
            padding: "10px",
            border: "none",
            height: "34px",
            outline: "none",
            fontSize: "16px",
            borderRadius: "4px",
            backgroundColor: "transparent",
          }}
        />
      </div>

      {/* Weather card */}
      {weatherData && (
        <div
          style={{
            position: "absolute",
            top: 55,
            left: "clamp(44px, 23vw, 110px)",
            transform: "translateX(-50%)",
            zIndex: 10,
            background: weatherData.error
              ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
              : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            padding: "12px 16px",
            borderRadius: "8px",
            width: "200px",
            maxWidth: "90%",
            color: "white",
            fontFamily: "Arial, sans-serif",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
            <div>
              <div style={{ fontSize: "11px", opacity: 0.9, marginBottom: "2px" }}>
                {searchedLocation?.split(",")[0] || "Location"}
              </div>
              <div style={{ fontSize: "28px", fontWeight: "bold", lineHeight: "1" }}>
                {weatherData.temp}{weatherData.error ? "" : "°F"}
              </div>
            </div>
            {!weatherData.error && weatherData.icon && (
              <img
                src={weatherData.icon.startsWith('http') ? weatherData.icon : `https:${weatherData.icon}`}
                alt={weatherData.description}
                style={{ width: "50px", height: "50px", marginTop: "-5px" }}
              />
            )}
          </div>
          <div style={{ fontSize: "13px", textTransform: "capitalize", marginBottom: "8px", opacity: 0.95 }}>
            {weatherData.description}
          </div>
          {!weatherData.error && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", opacity: 0.9 }}>
                <div>
                  <span style={{ opacity: 0.8 }}>Feels like:</span> {weatherData.feelsLike}°F
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", opacity: 0.9, marginTop: "4px" }}>
                <div>
                  <span style={{ opacity: 0.8 }}>💧</span> {weatherData.humidity}%
                </div>
                <div>
                  <span style={{ opacity: 0.8 }}>💨</span> {weatherData.windSpeed} mph
                </div>
              </div>
            </>
          )}
          <button
            onClick={() => setWeatherData(null)}
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "50%",
              width: "20px",
              height: "20px",
              color: "white",
              cursor: "pointer",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Device type filter - only PPA types */}
      <select
        className={styles.customs_elect}
        value={selectedDeviceType}
        onChange={handleSelectChange}
      >
        <option value="All">All Devices</option>
        <option value="Hoymiles">Hoymiles</option>
        <option value="Sol-Ark">Sol-Ark</option>
        <option value="Enphase">Enphase</option>
        <option value="Franklin">Franklin</option>
      </select>

      <div
        ref={mapContainerRef}
        style={{
          width: "100%",
          height: "393px",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      />
    </>
  );
};

export default PPAMap;
